// Reine Spiel-Logik von "SiGeKo Run" — ohne DOM, ohne Canvas, ohne Ton.
//
// Die Komponente (SigekoRun.tsx) ruft pro Frame stepRun() auf und reagiert auf
// die zurueckgegebenen Ereignisse (piepen, Nacht kippen, Rekord speichern).
// Zufall kommt als Funktion herein, damit Tests deterministisch laufen.
//
// Alle Werte sind "pro Frame bei 60 fps" wie beim Chrome-Dino; stepRun rechnet
// dt in Frames um (f = dt*60), damit das Spiel auf 120-Hz-Bildschirmen nicht
// doppelt so schnell laeuft.

import { BAGS, CONE, FENCE, HOOK, S, type SpriteRows } from './sprites';

export type Random = () => number;

export const W = 600;
export const H = 150;
export const GROUND = 132;

/** Startposition des Laeufers. */
export const RUNNER_X = 50;
/** Anfangstempo (px pro Frame). */
export const SPEED_START = 6;
/** Beschleunigung pro Frame. Kein Deckel wie beim Dino (13): es wird immer schneller. */
export const SPEED_ACCEL = 0.0012;
/** 32 ist nur die Grenze, ab der die Kollision Hindernisse ueberspringen koennte. */
export const SPEED_LIMIT = 32;
/** Schwerkraft pro Frame. */
export const GRAVITY = 0.6;
/** Absprung-Geschwindigkeit (voller Sprung). */
export const JUMP_VY = -10.6;
/** Kurz getippt: die Aufwaertsbewegung wird beim Loslassen hierauf gekappt. */
export const SHORT_JUMP_VY = -7;
/** Ein voller Sprung dauert 34 Frames — der Freiraum hinter jedem Hindernis muss laenger sein. */
export const JUMP_FRAMES = 34;
/** Mindest-Freiraum hinter einem Hindernis in Frames Fahrt. */
export const FREE_MIN_FRAMES = 36;
/** Zufaelliger Zuschlag zum Freiraum (Frames). */
export const FREE_RANDOM_FRAMES = 34;
/** Zu Beginn (Punkte < 150) bekommt jede Luecke 100 px extra. */
export const FREE_EARLY_BONUS = 100;
export const FREE_EARLY_BONUS_UNTIL = 150;
/** Punkte = zurueckgelegte Strecke / 12. */
export const DIST_PER_POINT = 12;
/** Alle 100 Punkte blinkt der Zaehler und es piept. */
export const MILESTONE_EVERY = 100;
/** Alle 700 Punkte kippt Tag/Nacht. */
export const NIGHT_EVERY = 700;
/** Abstand des ersten Hindernisses. */
export const FIRST_GAP = 420;

export type ObstacleKind = 'hook' | 'pit' | 'fence' | 'cone' | 'bags';

export interface Obstacle {
    k: ObstacleKind;
    x: number;
    /** Oberkante. Beim Schacht (pit) ungenutzt. */
    y: number;
    w: number;
    h: number;
    /** Wie oft das Sprite nebeneinander gezeichnet wird. */
    n: number;
    /** Sprite-Zeilen; der Schacht hat keine. */
    rows: SpriteRows | null;
    /** Schwebt (Kranhaken) — y kommt nicht vom Boden. */
    fly: boolean;
}

export interface Cloud {
    x: number;
    y: number;
}

export interface GroundDot {
    x: number;
    w: number;
}

export interface RunState {
    x: number;
    y: number;
    vy: number;
    duck: boolean;
    onGround: boolean;
    speed: number;
    dist: number;
    score: number;
    obs: Obstacle[];
    clouds: Cloud[];
    craneX: number;
    /** Strecke bis zum naechsten Hindernis. */
    nextGap: number;
    /** Lauf-Animation (RUN_A/RUN_B im Wechsel). */
    frame: number;
    /** Gesamtzeit in Sekunden (fuer das Blinken des Zaehlers). */
    t: number;
    over: boolean;
    started: boolean;
    /** Restdauer des Zaehler-Blinkens in Sekunden. */
    flash: number;
    groundDots: GroundDot[];
    night: boolean;
}

export interface StepEvents {
    /** 100er-Marke erreicht: Zaehler blinkt, Doppel-Piep. */
    milestone?: true;
    /** Tag/Nacht ist gekippt; state.night traegt den neuen Wert. */
    nightToggled?: true;
    /** Der Laeufer ist in dieser Runde gestorben. */
    died?: true;
}

export interface JumpEvents {
    /** Nach dem Tod: neue Runde begonnen (Zustand zurueckgesetzt, Nacht aus). */
    restarted?: true;
    /** Tatsaechlich abgesprungen (Sprung-Piep). */
    jumped?: true;
}

export interface Hitbox {
    x: number;
    y: number;
    w: number;
    h: number;
}

export function createRunState(random: Random = Math.random): RunState {
    const st: RunState = {
        x: RUNNER_X,
        y: GROUND,
        vy: 0,
        duck: false,
        onGround: true,
        speed: SPEED_START,
        dist: 0,
        score: 0,
        obs: [],
        clouds: [{ x: 120, y: 30 }, { x: 380, y: 18 }, { x: 560, y: 44 }],
        craneX: 420,
        nextGap: FIRST_GAP,
        frame: 0,
        t: 0,
        over: false,
        started: false,
        flash: 0,
        groundDots: [],
        night: false,
    };
    for (let i = 0; i < 24; i++) st.groundDots.push({ x: random() * W, w: 2 + random() * 4 });
    return st;
}

/** Setzt ein Hindernis an den rechten Rand und bestimmt die Luecke bis zum naechsten. */
export function spawnObstacle(st: RunState, random: Random = Math.random): Obstacle {
    const r = random();
    const sc = st.score;
    let o: Obstacle;
    if (sc > 250 && r < 0.22) {
        o = { k: 'hook', rows: HOOK, y: random() < 0.5 ? GROUND - 54 : GROUND - 24, fly: true, x: 0, w: 0, h: 0, n: 1 };
    } else if (sc > 120 && r < 0.4) {
        o = { k: 'pit', rows: null, w: 34 + Math.floor(random() * 24), fly: false, x: 0, y: GROUND, h: 0, n: 1 };
    } else if (r < 0.6) {
        o = { k: 'fence', rows: FENCE, n: 1 + Math.floor(random() * (sc > 400 ? 3 : 2)), fly: false, x: 0, y: 0, w: 0, h: 0 };
    } else if (r < 0.8) {
        o = { k: 'cone', rows: CONE, n: 1 + Math.floor(random() * 3), fly: false, x: 0, y: 0, w: 0, h: 0 };
    } else {
        o = { k: 'bags', rows: BAGS, n: 1, fly: false, x: 0, y: 0, w: 0, h: 0 };
    }
    o.x = W + 20;
    if (o.rows) {
        // Breite nach der ERSTEN Zeile, wie im Prototyp (Saecke haben unten eine laengere Zeile).
        o.w = o.rows[0].length * S * o.n;
        o.h = o.rows.length * S;
        if (!o.fly) o.y = GROUND - o.h;
    }
    st.obs.push(o);
    // Freiraum HINTER dem Hindernis: mindestens 36 Frames Fahrt (Sprung dauert 34), plus Zufall.
    const free = st.speed * (FREE_MIN_FRAMES + random() * FREE_RANDOM_FRAMES) + (sc < FREE_EARLY_BONUS_UNTIL ? FREE_EARLY_BONUS : 0);
    st.nextGap = (o.w || 40) + free;
    return o;
}

export function hitbox(st: RunState): Hitbox {
    const w = st.duck ? 32 : 22;
    const h = st.duck ? 22 : 38;
    const x = st.x + (st.duck ? 0 : 5);
    const y = st.duck ? GROUND - 22 : st.y - 38;
    return { x, y, w, h };
}

function die(st: RunState): StepEvents {
    st.over = true;
    return { died: true };
}

/**
 * Ein Simulationsschritt. dtSeconds ist die echte Zeit seit dem letzten Frame
 * (die Komponente deckelt sie auf 0,05 s, damit ein Tab-Wechsel keinen Riesensprung macht).
 */
export function stepRun(st: RunState, dtSeconds: number, random: Random = Math.random): StepEvents {
    const ev: StepEvents = {};
    st.t += dtSeconds;
    if (!st.started || st.over) return ev;
    const f = dtSeconds * 60; // Dino-Skalierung: Werte pro Frame bei 60 fps
    st.speed = Math.min(SPEED_LIMIT, st.speed + SPEED_ACCEL * f);
    st.dist += st.speed * f;
    const ns = Math.floor(st.dist / DIST_PER_POINT);
    if (ns !== st.score) {
        st.score = ns;
        if (st.score > 0 && st.score % MILESTONE_EVERY === 0) {
            st.flash = 0.5;
            ev.milestone = true;
        }
        if (st.score > 0 && st.score % NIGHT_EVERY === 0) {
            st.night = !st.night;
            ev.nightToggled = true;
        }
    }
    // Sprung
    if (!st.onGround) {
        st.vy += GRAVITY * f;
        st.y += st.vy * f;
        if (st.y >= GROUND) {
            st.y = GROUND;
            st.vy = 0;
            st.onGround = true;
        }
    }
    // Welt
    for (const d of st.groundDots) {
        d.x -= st.speed * f;
        if (d.x < -8) {
            d.x = W + random() * 40;
            d.w = 2 + random() * 4;
        }
    }
    for (const c of st.clouds) {
        c.x -= st.speed * f * 0.18;
        if (c.x < -40) {
            c.x = W + 40;
            c.y = 10 + random() * 50;
        }
    }
    st.craneX -= st.speed * f * 0.08;
    if (st.craneX < -60) st.craneX = W + 120;
    st.nextGap -= st.speed * f;
    if (st.nextGap <= 0) spawnObstacle(st, random);
    const hb = hitbox(st);
    for (let i = st.obs.length - 1; i >= 0; i--) {
        const o = st.obs[i];
        o.x -= st.speed * f;
        if (o.x + (o.w || 40) < -10) {
            st.obs.splice(i, 1);
            continue;
        }
        if (o.k === 'pit') {
            // Der Schacht toetet nur am Boden — in der Luft fliegt man drueber.
            if (st.onGround && hb.x + hb.w - 8 > o.x && hb.x + 8 < o.x + o.w) return { ...ev, ...die(st) };
        } else {
            // 3 px Toleranz rundum, damit ein Pixel Beruehrung nicht schon toetet.
            const ox = o.x + 3, oy = o.y + 3, ow = o.w - 6, oh = o.h - 6;
            if (hb.x < ox + ow && hb.x + hb.w > ox && hb.y < oy + oh && hb.y + hb.h > oy) return { ...ev, ...die(st) };
        }
    }
    if (st.flash > 0) st.flash -= dtSeconds;
    st.frame += dtSeconds * 10;
    return ev;
}

/**
 * Leertaste / Pfeil hoch / Tippen. Nach dem Tod startet eine neue Runde
 * (der Zustand wird IN PLACE zurueckgesetzt, damit die Komponente ihre
 * Referenz behalten kann); sonst wird abgesprungen, falls am Boden und nicht geduckt.
 */
export function jump(st: RunState, random: Random = Math.random): JumpEvents {
    if (st.over) {
        Object.assign(st, createRunState(random));
        st.started = true;
        return { restarted: true };
    }
    if (!st.started) st.started = true;
    if (st.onGround && !st.duck) {
        st.onGround = false;
        st.vy = JUMP_VY;
        return { jumped: true };
    }
    return {};
}

/** Kurz getippt = kurzer Hopser, gehalten = voller Sprung (wie beim Dino). */
export function jumpRelease(st: RunState): void {
    if (!st.onGround && st.vy < SHORT_JUMP_VY) st.vy = SHORT_JUMP_VY;
}

/**
 * Pfeil runter: ducken; in der Luft zusaetzlich schneller fallen.
 * Der Finger-nach-unten-Zieher am Touchscreen setzt nur `duck` (fastFall=false).
 */
export function duckPress(st: RunState, fastFall = true): void {
    st.duck = true;
    if (fastFall && !st.onGround) st.vy += 2;
}

export function duckRelease(st: RunState): void {
    st.duck = false;
}
